import { API_BASE_URL } from "../config/api";
import { authFetch, safeJson } from "../utils/auth";

import type {
  ExtendRequestBody,
  ExtendResponse,
  ExtensionTiersResponse,
  VerifyExtensionResponse,
  ChatMatch,
} from "../types/chatExtension";

/** Thrown when the backend returns an explicit error */
export class ChatExtApiError extends Error {
  code: string;
  constructor(message: string, code = "UNKNOWN") {
    super(message);
    this.name = "ChatExtApiError";
    this.code = code;
  }
}

async function req<T>(url: string, options: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await authFetch(url, options, 15000);
  } catch (e: any) {
    const reason = e?.name === "AbortError" ? "timeout" : "network";
    throw new ChatExtApiError(
      reason === "timeout"
        ? "The server took too long to respond."
        : "Could not reach the server.",
      "NETWORK",
    );
  }

  const json = await safeJson(res);
  if (!json) {
    throw new ChatExtApiError("Empty response from server.", "PARSE");
  }

  // Handle { status, code, msg } envelope AND raw JSON
  if (typeof json.status === "number" && json.status === 0) {
    throw new ChatExtApiError(
      json.msg || json.message || "Request refused.",
      json.code || "REFUSED",
    );
  }
  if (!res.ok) {
    throw new ChatExtApiError(
      json.message || json.msg || `Request failed (${res.status}).`,
      `HTTP_${res.status}`,
    );
  }
  return (json.data !== undefined ? json.data : json) as T;
}

/* ── Endpoints  */

/** GET /matches/:matchId/extension-tiers */
export function getExtensionTiers(
  matchId: string,
): Promise<ExtensionTiersResponse> {
  return req<ExtensionTiersResponse>(
    `${API_BASE_URL}/matches/${matchId}/extension-tiers`,
  );
}

/** POST /matches/:matchId/extend */
export function startExtension(
  matchId: string,
  body: ExtendRequestBody,
): Promise<ExtendResponse> {
  return req<ExtendResponse>(`${API_BASE_URL}/matches/${matchId}/extend`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** GET /matches/my-matches */
export function getMyMatches(): Promise<ChatMatch[]> {
  return req<ChatMatch[]>(`${API_BASE_URL}/matches/my-matches`);
}
/** POST /matches/:matchId/extend/verify */
export function verifyExtension(
  matchId: string,
  reference: string,
): Promise<VerifyExtensionResponse> {
  return req<VerifyExtensionResponse>(
    `${API_BASE_URL}/matches/${matchId}/extend/verify`,
    {
      method: "POST",
      body: JSON.stringify({ reference }),
    },
  );
}
